import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import apiService from '../services/apiService';

const ROTULO_VEREDITO = {
  comprar: { label: 'Comprar', className: 'bg-green-50 text-green-700 border-green-200' },
  compra_teste: { label: 'Compra de Teste', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  nao_comprar: { label: 'Não Comprar', className: 'bg-red-50 text-red-700 border-red-200' },
};

function Badge({ valor }) {
  if (!valor) return <span className="text-gray-400 text-xs">—</span>;
  const info = ROTULO_VEREDITO[valor] || { label: valor, className: 'bg-gray-50 text-gray-700 border-gray-200' };
  return (
    <span className={`inline-block text-xs border rounded px-2 py-0.5 ${info.className}`}>
      {info.label}
    </span>
  );
}

function formatarDataHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function AvaliacoesHistorico() {
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const [termoPrincipal, setTermoPrincipal] = useState('');
  const [veredito, setVeredito] = useState('');
  const [decisaoHumana, setDecisaoHumana] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const filtros = { page, pageSize };
      if (termoPrincipal.trim()) filtros.termoPrincipal = termoPrincipal.trim();
      if (veredito) filtros.veredito = veredito;
      if (decisaoHumana) filtros.decisaoHumana = decisaoHumana;

      const resp = await apiService.avaliacaoCompra.historico(filtros);
      setItens(resp.data.data?.itens ?? []);
      setTotal(resp.data.data?.total ?? 0);
    } catch {
      setErro('Erro ao carregar histórico de avaliações.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, termoPrincipal, veredito, decisaoHumana]);

  useEffect(() => { carregar(); }, [carregar]);

  function handleFiltrar(e) {
    e.preventDefault();
    setPage(1);
    carregar();
  }

  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <form onSubmit={handleFiltrar} className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-gray-200 rounded-xl px-4 py-3">
        <label className="text-sm text-gray-600 flex flex-col gap-1">
          Termo principal
          <input
            type="text"
            placeholder="ex: ventilador de teto"
            value={termoPrincipal}
            onChange={e => setTermoPrincipal(e.target.value)}
            className="w-56 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
          />
        </label>
        <label className="text-sm text-gray-600 flex flex-col gap-1">
          Veredito
          <select
            value={veredito}
            onChange={e => setVeredito(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
          >
            <option value="">Todos</option>
            <option value="comprar">Comprar</option>
            <option value="compra_teste">Compra de Teste</option>
            <option value="nao_comprar">Não Comprar</option>
          </select>
        </label>
        <label className="text-sm text-gray-600 flex flex-col gap-1">
          Decisão humana
          <select
            value={decisaoHumana}
            onChange={e => setDecisaoHumana(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
          >
            <option value="">Todas</option>
            <option value="comprar">Comprar</option>
            <option value="compra_teste">Compra de Teste</option>
            <option value="nao_comprar">Não Comprar</option>
          </select>
        </label>
        <button
          type="submit"
          className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
        >
          Filtrar
        </button>
      </form>

      <div className="relative bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">Carregando...</div>
        )}
        {erro && (
          <div className="px-5 py-4 text-sm text-red-600 border-b border-gray-100">{erro}</div>
        )}
        {!loading && !erro && itens.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">Nenhuma avaliação encontrada.</div>
        )}
        {!loading && itens.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-medium">Termo Principal</th>
                  <th className="text-left px-4 py-3 font-medium">Âncora</th>
                  <th className="text-left px-4 py-3 font-medium">GMM Estimado</th>
                  <th className="text-left px-4 py-3 font-medium">Sugestão</th>
                  <th className="text-left px-4 py-3 font-medium">Veredito (IA)</th>
                  <th className="text-left px-4 py-3 font-medium">Decisão</th>
                  <th className="text-left px-4 py-3 font-medium">Decidido por</th>
                  <th className="text-left px-4 py-3 font-medium">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {itens.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.termo_principal || '—'}</div>
                      <div className="text-xs text-gray-400 max-w-xs truncate">{item.texto_digitado}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{item.codigo_ancora || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{item.gmm_estimado != null ? Number(item.gmm_estimado).toFixed(2) : '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.quantidade_sugerida != null ? `${item.quantidade_sugerida} un.` : '—'}
                      {item.duracao_meses != null && (
                        <div className="text-xs text-gray-400">{Number(item.duracao_meses).toFixed(1)} meses</div>
                      )}
                    </td>
                    <td className="px-4 py-3"><Badge valor={item.veredito} /></td>
                    <td className="px-4 py-3"><Badge valor={item.decisao_humana} /></td>
                    <td className="px-4 py-3 text-gray-700">{item.decidido_por || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatarDataHora(item.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
            <span>{total} avaliação(ões) — página {page} de {totalPaginas}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft size={14} />Anterior
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPaginas, p + 1))}
                disabled={page >= totalPaginas}
                className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Próxima<ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
